import React, { useState } from "react";
import {
  CartItem,
  RequestDetails,
  CartAllocation,
} from "../../../redux/AllocationRequest";
import { StepIndicator } from "../../../components/StepIndicator";
import { Step1 } from "./CreateRequest/Step1";
import { Step2 } from "./CreateRequest/Step2";
import { Step3 } from "./CreateRequest/Step3";
import { Step4 } from "./CreateRequest/Step4";
import { useSelector } from "react-redux";
import "./index.css";
import { RequestTable } from "./RequestTable";
import { toast } from "react-toastify";
import { useCreateOrderRequestMutation } from "../../../redux/services/allocationService";
import { BulkCart, BulkCustomData, BulkGridData, BulkProduct } from "./CreateRequest/Step3/BulkAllocationGrid";

const AllocationRequestWrapper: React.FC = () => {
  const [view, setView] = useState<"list" | "new">("list");
  const [step, setStep] = useState(1);

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails?.id,
  );

  const [createOrderRequest, { isLoading: createLoading }] =
    useCreateOrderRequestMutation();

  const [reqType, setReqType] = useState("");
  const [details, setDetails] = useState<RequestDetails>({
    department: "",
    requestedBy: "",
    targetDate: "",
    notes: "",
  });
  const [items, setItems] = useState<CartItem[]>([]);
  const [cartAllocations, setCartAllocations] = useState<CartAllocation[]>([]);

  // ─── Bulk Cart Allocation state ───────────────────────────────────────────
  const [bulkGridData, setBulkGridData] = useState<BulkGridData>({});
  const [bulkCustomData, setBulkCustomData] = useState<BulkCustomData>({});
  const [bulkProducts, setBulkProducts] = useState<BulkProduct[]>([]);
  const [bulkCarts, setBulkCarts] = useState<BulkCart[]>([]);

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("All Status");
  const [filterDept, setFilterDept] = useState("All Departments");
  const [filterType, setFilterType] = useState("All Types");

  const resetWizard = () => {
    setStep(1);
    setReqType("");
    setDetails({ department: "", requestedBy: "", targetDate: "", notes: "" });
    setItems([]);
    setCartAllocations([]);
    setBulkGridData({});
    setBulkCustomData({});
    setBulkProducts([]);
    setBulkCarts([]);
  };

  const mapCustomItem = (ci: any) => ({
    menuItemId: ci.menuItemId || ci.itemId,
    displayName: ci.displayName,
    unitPrice: ci.unitPrice ?? ci.price ?? 0,
    productId: ci.productId,
    quantity: ci.quantity ?? 1,
    isAddOn: ci.isAddOn || false,
    skuId: ci.skuId || "",
    ...(ci.tinyId !== undefined ? { tinyId: ci.tinyId } : {}),
  });

  const buildProductPayload = (item: CartItem) => {
    const base: any = {
      displayName: item.name,
      skuId: item.skuId || "",
      price: item.price || 0,
      itemId: item.id,
      productId: item.productId || item.id,
      tinyId: item.tinyId || "",
      itemType: item.itemType || "MenuItem",
      quantity: item.qty,
      tier: item.tier || "",
    };

    if (item.itemType === "CustomMenu") {
      const rawCustomItems = (item as any).customItems;
      if (Array.isArray(rawCustomItems) && rawCustomItems.length > 0) {
        base.customItems = rawCustomItems.map(mapCustomItem);
        base.unitTotalPrice = base.customItems.reduce(
          (sum: number, ci: any) => sum + ci.unitPrice * ci.quantity, 0,
        );
      } else {
        base.customItems = [];
        base.unitTotalPrice = (item as any).customItemTotal ?? item.price ?? 0;
      }
      base.portionSize = (item as any).portionLabel || "";
      base.perItemManualDiscount = (item as any).perItemManualDiscount || 0;
    }
    return base;
  };

  const buildProductsFromCartAllocations = (allocations: CartAllocation[]): CartItem[] => {
    const productMap = new Map<string, CartItem>();
    allocations.forEach((cart) => {
      cart.allocations.forEach((alloc: any) => {
        const key = alloc.itemId;
        const existing = productMap.get(key);
        if (existing) {
          existing.qty += alloc.quantity;
          if (alloc.itemType === "CustomMenu" && Array.isArray(alloc.customItems) && alloc.customItems.length > 0) {
            const existingCustomItems = (existing as any).customItems || [];
            const customItemMap = new Map();
            existingCustomItems.forEach((item: any) => customItemMap.set(item.menuItemId || item.itemId, { ...item }));
            alloc.customItems.forEach((newItem: any) => {
              const itemKey = newItem.menuItemId || newItem.itemId;
              if (!customItemMap.has(itemKey)) {
                customItemMap.set(itemKey, {
                  menuItemId: newItem.menuItemId || newItem.itemId,
                  displayName: newItem.displayName,
                  unitPrice: newItem.unitPrice ?? newItem.price ?? 0,
                  productId: newItem.productId,
                  quantity: newItem.quantity ?? 1,
                  isAddOn: newItem.isAddOn || false,
                  skuId: newItem.skuId || "",
                  ...(newItem.tinyId !== undefined ? { tinyId: newItem.tinyId } : {}),
                });
              }
            });
            (existing as any).customItems = Array.from(customItemMap.values());
            (existing as any).customItemTotal = (existing as any).customItems.reduce(
              (sum: number, item: any) => sum + item.unitPrice * (item.quantity || 1), 0,
            );
          }
          productMap.set(key, existing);
        } else {
          const newProduct: any = {
            id: alloc.itemId, name: alloc.productName, skuId: alloc.skuId, price: alloc.price,
            tier: alloc.tier, qty: alloc.quantity, productId: alloc.productId || alloc.itemId,
            imageUrl: "", tinyId: "", itemType: alloc.itemType || "MenuItem",
          };
          if (alloc.itemType === "CustomMenu" && Array.isArray(alloc.customItems) && alloc.customItems.length > 0) {
            newProduct.customItems = alloc.customItems.map((ci: any) => ({
              menuItemId: ci.menuItemId || ci.itemId, displayName: ci.displayName,
              unitPrice: ci.unitPrice ?? ci.price ?? 0, productId: ci.productId,
              quantity: ci.quantity ?? 1, isAddOn: ci.isAddOn || false, skuId: ci.skuId || "",
              ...(ci.tinyId !== undefined ? { tinyId: ci.tinyId } : {}),
            }));
            newProduct.customItemTotal = newProduct.customItems.reduce(
              (sum: number, item: any) => sum + item.unitPrice * (item.quantity || 1), 0,
            );
            newProduct.portionLabel = alloc.portionSize;
            newProduct.perItemManualDiscount = alloc.perItemManualDiscount || 0;
          }
          productMap.set(key, newProduct);
        }
      });
    });
    return Array.from(productMap.values());
  };

const buildAllocationPayload = (alloc: any, isBulkRequest: boolean = false) => {
  const base: any = {
    itemId: alloc.itemId, 
    productId: alloc.productId || alloc.itemId,
    productName: alloc.productName, 
    skuId: alloc.skuId, 
    price: alloc.price,
    tier: alloc.tier, 
    quantity: alloc.quantity, 
    subtotal: alloc.subtotal,
  };
  
  // Only add channels if not a bulk request
  if (!isBulkRequest && alloc.channels) {
    base.channels = alloc.channels;
  }
  
  if (alloc.itemType === "CustomMenu" || (Array.isArray(alloc.customItems) && alloc.customItems.length > 0)) {
    base.itemType = "CustomMenu";
    base.unitTotalPrice = alloc.unitTotalPrice ?? alloc.price;
    base.portionSize = alloc.portionSize || "";
    base.perItemManualDiscount = alloc.perItemManualDiscount || 0;
    base.customItems = Array.isArray(alloc.customItems) && alloc.customItems.length > 0
      ? alloc.customItems.map(mapCustomItem)
      : [];
  }
  return base;
};

// ─── Build bulk payload ───────────────────────────────────────────────────
const buildBulkPayload = () => {
  // Build flat list of cart allocations from the grid
  const cartAllocationsPayload: any[] = [];
  let totalQuantity = 0;
  let totalItems = 0;

  bulkCarts.forEach((cart) => {
    const cartAllocations: any[] = [];
    let cartTotalQuantity = 0;
    let cartTotalAmount = 0;

    bulkProducts.forEach((product) => {
      const qty = bulkGridData[cart.cartId]?.[product.itemId] || 0;
      if (qty === 0) return;

      const subtotal = product.price * qty;
      const alloc: any = {
        itemId: product.itemId,
        productId: product.productId || product.itemId,
        productName: product.displayName,
        skuId: product.skuId || "",
        price: product.price,
        tier: product.tier || "",
        quantity: qty,
        subtotal,
      };
      // No channels field for bulk requests

      if (product.itemType === "CustomMenu") {
        const customConfig = bulkCustomData[product.itemId];
        alloc.itemType = "CustomMenu";
        alloc.customItems = customConfig?.customItems?.map(mapCustomItem) || [];
        alloc.unitTotalPrice = customConfig?.customItemTotal ?? product.price;
        alloc.portionSize = "";
        alloc.perItemManualDiscount = 0;
      }

      cartAllocations.push(alloc);
      cartTotalQuantity += qty;
      cartTotalAmount += subtotal;
    });

    if (cartAllocations.length > 0) {
      cartAllocationsPayload.push({
        cartId: cart.cartId,
        cartName: cart.displayName,
        cartEmail: "",
        cartPhone: "",
        cartType: cart.cartType || "",
        cartState: cart.cartState || "Active",
        allocations: cartAllocations,
        totalQuantity: cartTotalQuantity,
        totalAmount: cartTotalAmount,
      });
      totalQuantity += cartTotalQuantity;
      totalItems += cartAllocations.length;
    }
  });

  // Build aggregated products list
  const productMap = new Map<string, any>();
  bulkProducts.forEach((product) => {
    const totalQty = bulkCarts.reduce(
      (sum, cart) => sum + (bulkGridData[cart.cartId]?.[product.itemId] || 0), 0,
    );
    if (totalQty === 0) return;

    const customConfig = bulkCustomData[product.itemId];
    const prod: any = {
      displayName: product.displayName,
      skuId: product.skuId || "",
      price: product.price || 0,
      itemId: product.itemId,
      productId: product.productId || product.itemId,
      tinyId: product.tinyId || "",
      itemType: product.itemType || "MenuItem",
      quantity: totalQty,
      tier: product.tier || "",
    };

    if (product.itemType === "CustomMenu" && customConfig?.customItems?.length) {
      prod.customItems = customConfig.customItems.map(mapCustomItem);
      prod.unitTotalPrice = customConfig.customItemTotal ?? product.price;
      prod.portionSize = "";
      prod.perItemManualDiscount = 0;
    }

    productMap.set(product.itemId, prod);
  });

  return {
    productsPayload: Array.from(productMap.values()),
    cartAllocationsPayload,
    totalItems,
    totalQuantity,
    totalAmount: cartAllocationsPayload.reduce((s, c) => s + c.totalAmount, 0),
  };
};

const submitRequest = async () => {
  if (!reqType) { toast.error("Please select request type"); return; }
  if (!details.department?.trim()) { toast.error("Please enter department name"); return; }
  if (!details.requestedBy?.trim()) { toast.error("Please enter requester name"); return; }
  if (!details.targetDate) { toast.error("Please select target date"); return; }

  // Map request type to underscore format
  const getRequestTypeValue = (type: string): string => {
    switch (type) {
      case "Cart Allocation":
        return "Cart_Allocation";
      case "Bulk Cart Allocation":
        return "Bulk_Cart_Allocation";
      default:
        return "Product_Quantity";
    }
  };

  let productsPayload: any[] = [];
  let cartAllocationsPayload: any[] = [];
  let totalItems = 0;
  let totalQuantity = 0;
  let totalAmount = 0;

  if (reqType === "Bulk Cart Allocation") {
    const built = buildBulkPayload();
    if (built.productsPayload.length === 0) {
      toast.error("Please enter quantities for at least one product.");
      return;
    }
    // Validate custom menus have customItems configured
    for (const p of bulkProducts) {
      const totalQty = bulkCarts.reduce((s, c) => s + (bulkGridData[c.cartId]?.[p.itemId] || 0), 0);
      if (totalQty > 0 && p.itemType === "CustomMenu" && !bulkCustomData[p.itemId]?.customItems?.length) {
        toast.error(`"${p.displayName}" is a Custom Menu but has no custom items configured.`);
        return;
      }
    }
    productsPayload = built.productsPayload;
    cartAllocationsPayload = built.cartAllocationsPayload;
    totalItems = built.totalItems;
    totalQuantity = built.totalQuantity;
    totalAmount = built.totalAmount;

  } else if (reqType === "Cart Allocation") {
    if (cartAllocations.length === 0) { toast.error("Please allocate products to at least one cart"); return; }
    let hasInvalidAllocation = false;
    cartAllocations.forEach((cart) => {
      cart.allocations.forEach((product: any) => {
        const totalChannelQty = Object.values(product.channels as Record<string, number>).reduce((sum, qty) => sum + qty, 0);
        if (totalChannelQty !== product.quantity) {
          hasInvalidAllocation = true;
          toast.error(`Product "${product.productName}" in cart "${cart.cartName}" has mismatched channel allocation.`);
        }
        if (product.itemType === "CustomMenu" && (!Array.isArray(product.customItems) || product.customItems.length === 0)) {
          hasInvalidAllocation = true;
          toast.error(`Product "${product.productName}" in cart "${cart.cartName}" is a Custom Menu but has no custom items configured.`);
        }
      });
    });
    if (hasInvalidAllocation) return;

    const aggregated = buildProductsFromCartAllocations(cartAllocations);
    productsPayload = aggregated.map((item) => buildProductPayload(item));
    cartAllocationsPayload = cartAllocations.map((cart: any) => ({
      cartId: cart.cartId, 
      cartName: cart.cartName, 
      cartEmail: cart.cartEmail || "",
      cartPhone: cart.cartPhone || "", 
      cartType: cart.cartType || "", 
      cartState: cart.cartState || "Active",
      allocations: cart.allocations.map((alloc: any) => buildAllocationPayload(alloc)),
      totalAmount: cart.totalAmount, 
      totalQuantity: cart.totalQuantity,
    }));
    totalItems = cartAllocations.reduce((sum, cart) => sum + cart.allocations.length, 0);
    totalQuantity = cartAllocations.reduce((sum, cart) => sum + (cart.totalQuantity || 0), 0);
    totalAmount = cartAllocations.reduce((sum, cart) => sum + (cart.totalAmount || 0), 0);

  } else {
    if (items.length === 0) { toast.error("Please add at least one product"); return; }
    for (const item of items) {
      if (item.itemType === "CustomMenu" && (!Array.isArray((item as any).customItems) || (item as any).customItems.length === 0)) {
        toast.error(`"${item.name}" is a Custom Menu item but has no custom items configured.`);
        return;
      }
    }
    productsPayload = items.map((item) => buildProductPayload(item));
    totalItems = items.length;
    totalQuantity = items.reduce((sum, item) => sum + item.qty, 0);
    totalAmount = items.reduce((sum, item) => sum + (item.price || 0) * item.qty, 0);
  }

  const payload: any = {
    restaurantId,
    requestType: getRequestTypeValue(reqType), // Use mapped value
    department: details.department.trim(),
    requestedBy: details.requestedBy.trim(),
    targetDate: details.targetDate,
    notes: details.notes || "",
    status: "Pending",
    totalItems,
    totalQuantity,
    totalAmount,
    products: productsPayload,
  };

  if (reqType === "Cart Allocation") {
    payload.cartAllocations = cartAllocationsPayload;
  }
  
  if (reqType === "Bulk Cart Allocation") {
    payload.cartAllocations = cartAllocationsPayload;
  }

  console.log("📤 FULL PAYLOAD:", JSON.stringify(payload, null, 2));

  try {
    const response = await createOrderRequest(payload).unwrap();
    toast.success(response.message || "Request submitted successfully!");
    setView("list");
    resetWizard();
  } catch (error: any) {
    console.error("❌ Error submitting request:", error);
    toast.error(error?.data?.message || "Failed to submit request. Please try again.");
  }
};

  const handleViewRequest = (request: any) => {
    console.log("Viewing request:", request);
  };

  // Step label helper
  const getStep3Label = () => {
    if (reqType === "Cart Allocation") return "Allocate Products to Carts";
    if (reqType === "Bulk Cart Allocation") return "Bulk Allocation Grid";
    return "Add Items";
  };

  // ── LIST VIEW ─────────────────────────────────────────────────────────────
  if (view === "list")
    return (
      <div className="allocation-request-wrapper">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="mb-0">Department Requests</h1>
              <p className="text-muted mb-0">View and manage all production requests</p>
            </div>
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={() => { setView("new"); resetWizard(); }}
            >
              <span style={{ fontSize: 18, fontWeight: 300 }}>+</span> New Request
            </button>
          </div>
          <RequestTable
            search={search} filterStatus={filterStatus} filterDept={filterDept} filterType={filterType}
            onSearchChange={setSearch} onFilterStatusChange={setFilterStatus}
            onFilterDeptChange={setFilterDept} onFilterTypeChange={setFilterType}
            onViewRequest={handleViewRequest}
          />
        </div>
      </div>
    );

  // ── WIZARD VIEW ───────────────────────────────────────────────────────────
  return (
    <div className="allocation-request-wrapper">
      <div className="wizard-container">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h1 className="mb-0">New Request</h1>
            <p className="text-muted">
              Step {step} of 4:{" "}
              {["Select Request Type", "Request Details", getStep3Label(), "Review & Submit"][step - 1]}
            </p>
          </div>
          <button className="btn btn-outline-secondary" onClick={() => { setView("list"); resetWizard(); }}>
            ← Cancel
          </button>
        </div>

        <StepIndicator current={step} />

        <div className="card shadow-sm">
          <div className="card-body p-4">
            {step === 1 && <Step1 selected={reqType} onSelect={setReqType} />}
            {step === 2 && (
              <Step2 details={details} onChange={(k, v) => setDetails((d) => ({ ...d, [k]: v }))} />
            )}
            {step === 3 && (
              <Step3
                items={items}
                onAdd={(item) => setItems((prev) => [...prev, item])}
                onRemove={(id) => setItems((prev) => prev.filter((i) => i.id !== id))}
                restaurantId={restaurantId}
                requestType={reqType}
                onCartAllocationsChange={(allocations) => setCartAllocations(allocations)}
                initialCartAllocations={cartAllocations}
                bulkGridData={bulkGridData}
                bulkCustomData={bulkCustomData}
                onBulkGridChange={setBulkGridData}
                onBulkCustomChange={setBulkCustomData}
                onBulkProductsLoaded={setBulkProducts}
                onBulkCartsLoaded={setBulkCarts}
              />
            )}
            {step === 4 && (
              <Step4
                type={reqType}
                details={details}
                items={items}
                cartAllocations={cartAllocations}
                bulkGridData={bulkGridData}
                bulkCustomData={bulkCustomData}
                bulkProducts={bulkProducts}
                bulkCarts={bulkCarts}
              />
            )}
          </div>
        </div>

        <div className="d-flex justify-content-between mt-4">
          <button
            className="btn btn-outline-secondary"
            onClick={() => (step > 1 ? setStep((s) => s - 1) : setView("list"))}
          >
            ← Back
          </button>
          {step < 4 ? (
            <button
              className="btn btn-primary"
              disabled={step === 1 && !reqType}
              onClick={() => setStep((s) => s + 1)}
            >
              Next →
            </button>
          ) : (
            <button className="btn btn-primary" onClick={submitRequest} disabled={createLoading}>
              {createLoading ? "Submitting..." : "✓ Submit Request"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllocationRequestWrapper;